// ========================================
// PROJECT MANAGER
// Gestionnaire des projets
// ========================================

import React, { useState, useEffect } from 'react';
import AdminLayout from '../../../components/admin/Layout/AdminLayout';
import useProjects from '../../../hooks/useProjects';
import { ImageUploader, RichTextEditor } from '../../../components/admin/Editor';
import { slugify } from '../../../utils';
import {
  FiPlus,
  FiEdit2,
  FiTrash2,
  FiEye,
  FiSearch,
  FiX,
  FiSave,
  FiLoader,
  FiExternalLink,
  FiStar,
  FiCalendar,
  FiFolder,
  FiImage,
  FiArrowLeft,
  FiGrid,
  FiList
} from 'react-icons/fi';
import './ProjectManager.css';

// Catégories par défaut
const CATEGORIES = [
  { value: 'ong', label: 'ONG' },
  { value: 'association', label: 'Association' },
  { value: 'media', label: 'Médias' },
  { value: 'agence', label: 'Agence' },
  { value: 'personnel', label: 'Personnel' },
  { value: 'autre', label: 'Autre' }
];

const STATUS_OPTIONS = [
  { value: 'active', label: 'En cours', color: 'green' },
  { value: 'completed', label: 'Terminé', color: 'blue' },
  { value: 'paused', label: 'En pause', color: 'orange' }
];

const ProjectManager = () => {
  const { projects, loading, createProject, editProject, removeProject } = useProjects();
  
  // États
  const [view, setView] = useState('list'); // list, grid, form
  const [editingProject, setEditingProject] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterCategory, setFilterCategory] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [saving, setSaving] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  // Détecter le changement de taille d'écran
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Formulaire vide
  const emptyForm = {
    title: '',
    slug: '',
    shortDescription: '',
    fullDescription: '',
    mainImage: null,
    gallery: [],
    category: 'autre',
    yearStart: new Date().getFullYear().toString(),
    yearEnd: '',
    role: '',
    externalLink: '',
    status: 'active',
    isFeatured: false,
    order: projects.length
  };

  const [formData, setFormData] = useState(emptyForm);

  // Filtrer les projets
  const filteredProjects = projects.filter(project => {
    const matchesSearch = project.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         project.shortDescription?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = filterCategory === 'all' || project.category === filterCategory;
    const matchesStatus = filterStatus === 'all' || project.status === filterStatus;
    
    return matchesSearch && matchesCategory && matchesStatus;
  });

  // Ouvrir le formulaire pour nouveau projet
  const handleNewProject = () => {
    setFormData({ ...emptyForm, order: projects.length });
    setEditingProject(null);
    setView('form');
  };

  // Ouvrir le formulaire pour éditer
  const handleEditProject = (project) => {
    setFormData({
      ...emptyForm,
      ...project
    });
    setEditingProject(project.id);
    setView('form');
  };

  // Mettre à jour le formulaire
  const updateFormData = (field, value) => {
    setFormData(prev => {
      const updated = { ...prev, [field]: value };
      
      // Auto-générer le slug si on modifie le titre
      if (field === 'title' && !editingProject) {
        updated.slug = slugify(value);
      }
      
      return updated;
    });
  };

  // Sauvegarder le projet
  const handleSave = async () => {
    if (!formData.title.trim()) {
      return;
    }

    setSaving(true);

    try {
      if (editingProject) {
        await editProject(editingProject, formData);
      } else {
        await createProject(formData);
      }
      setView('list');
      setFormData(emptyForm);
      setEditingProject(null);
    } catch (error) {
      console.error('Erreur:', error);
    } finally {
      setSaving(false);
    }
  };

  // Supprimer un projet
  const handleDelete = async (projectId) => {
    await removeProject(projectId);
    setShowDeleteConfirm(null);
  };

  // Annuler l'édition
  const handleCancel = () => {
    setView('list');
    setFormData(emptyForm);
    setEditingProject(null);
  };

  // Composant carte de projet (réutilisable)
  const ProjectCard = ({ project }) => (
    <div className="project-card">
      <div className="project-card-image">
        {project.mainImage ? (
          <img src={project.mainImage} alt={project.title} />
        ) : (
          <div className="no-image">
            <FiImage />
          </div>
        )}
        {project.isFeatured && (
          <span className="featured-badge">
            <FiStar />
          </span>
        )}
      </div>
      
      <div className="project-card-content">
        <div className="project-card-header">
          <span className={`status-badge ${project.status}`}>
            {STATUS_OPTIONS.find(s => s.value === project.status)?.label}
          </span>
          <span className="category-badge">
            {CATEGORIES.find(c => c.value === project.category)?.label}
          </span>
        </div>
        
        <h3>{project.title}</h3>
        <p>{project.shortDescription}</p>
        
        <div className="project-card-footer">
          <span className="year">
            {project.yearStart}{project.yearEnd ? ` - ${project.yearEnd}` : ''}
          </span>
          <div className="project-card-actions">
            <button 
              className="action-btn"
              onClick={() => handleEditProject(project)}
              title="Modifier"
            >
              <FiEdit2 />
            </button>
            <a 
              href={`/projets/${project.slug}`}
              target="_blank"
              rel="noopener noreferrer"
              className="action-btn"
              title="Voir"
            >
              <FiEye />
            </a>
            <button 
              className="action-btn danger"
              onClick={() => setShowDeleteConfirm(project.id)}
              title="Supprimer"
            >
              <FiTrash2 />
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  // Rendu du formulaire
  const renderForm = () => (
    <div className="project-form-container">
      {/* Header du formulaire */}
      <div className="form-header">
        <button className="back-btn" onClick={handleCancel}>
          <FiArrowLeft />
          Retour à la liste
        </button>
        <h2>{editingProject ? 'Modifier le projet' : 'Nouveau projet'}</h2>
      </div>

      <div className="project-form">
        {/* Colonne principale */}
        <div className="form-main">
          {/* Image principale */}
          <div className="form-card">
            <h3><FiImage /> Image principale</h3>
            <ImageUploader
              value={formData.mainImage}
              onChange={(url) => updateFormData('mainImage', url)}
              folder="portfolio/projects"
              placeholder="Image de couverture du projet"
            />
          </div>

          {/* Informations de base */}
          <div className="form-card">
            <h3><FiEdit2 /> Informations</h3>
            
            <div className="form-group">
              <label className="form-label">Titre du projet *</label>
              <input
                type="text"
                className="form-input"
                value={formData.title}
                onChange={(e) => updateFormData('title', e.target.value)}
                placeholder="Nom du projet"
              />
            </div>

            <div className="form-group">
              <label className="form-label">Slug (URL)</label>
              <div className="input-with-prefix">
                <span>/projets/</span>
                <input
                  type="text"
                  className="form-input"
                  value={formData.slug}
                  onChange={(e) => updateFormData('slug', slugify(e.target.value))}
                  placeholder="slug-du-projet"
                />
              </div>
            </div>

            <div className="form-group">
              <label className="form-label">Description courte</label>
              <textarea
                className="form-input form-textarea"
                value={formData.shortDescription}
                onChange={(e) => updateFormData('shortDescription', e.target.value)}
                placeholder="Brève description (affichée dans les cartes)"
                rows={3}
              />
            </div>

            <div className="form-group">
              <label className="form-label">Description complète</label>
              <RichTextEditor
                value={formData.fullDescription}
                onChange={(content) => updateFormData('fullDescription', content)}
                placeholder="Description détaillée du projet..."
                minHeight={200}
              />
            </div>
          </div>

          {/* Rôle et lien */}
          <div className="form-card">
            <h3><FiExternalLink /> Détails supplémentaires</h3>
            
            <div className="form-group">
              <label className="form-label">Votre rôle</label>
              <input
                type="text"
                className="form-input"
                value={formData.role}
                onChange={(e) => updateFormData('role', e.target.value)}
                placeholder="Ex: Fondateur, Directeur artistique, etc."
              />
            </div>

            <div className="form-group">
              <label className="form-label">Lien externe</label>
              <input
                type="url"
                className="form-input"
                value={formData.externalLink}
                onChange={(e) => updateFormData('externalLink', e.target.value)}
                placeholder="https://..."
              />
            </div>
          </div>
        </div>

        {/* Colonne latérale */}
        <div className="form-sidebar">
          {/* Publication */}
          <div className="form-card">
            <h3>Publication</h3>
            
            <div className="form-group">
              <label className="form-label">Statut</label>
              <select
                className="form-input"
                value={formData.status}
                onChange={(e) => updateFormData('status', e.target.value)}
              >
                {STATUS_OPTIONS.map(opt => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={formData.isFeatured}
                  onChange={(e) => updateFormData('isFeatured', e.target.checked)}
                />
                <FiStar />
                <span>Mettre en vedette</span>
              </label>
            </div>

            <button
              className="btn btn-primary btn-block"
              onClick={handleSave}
              disabled={saving || !formData.title.trim()}
            >
              {saving ? <FiLoader className="spinner" /> : <FiSave />}
              {editingProject ? 'Mettre à jour' : 'Créer le projet'}
            </button>
          </div>

          {/* Catégorie */}
          <div className="form-card">
            <h3><FiFolder /> Catégorie</h3>
            
            <div className="category-options">
              {CATEGORIES.map(cat => (
                <label 
                  key={cat.value} 
                  className={`category-option ${formData.category === cat.value ? 'selected' : ''}`}
                >
                  <input
                    type="radio"
                    name="category"
                    value={cat.value}
                    checked={formData.category === cat.value}
                    onChange={(e) => updateFormData('category', e.target.value)}
                  />
                  <span>{cat.label}</span>
                </label>
              ))}
            </div>
          </div>

          {/* Période */}
          <div className="form-card">
            <h3><FiCalendar /> Période</h3>
            
            <div className="form-row two-cols">
              <div className="form-group">
                <label className="form-label">Début</label>
                <input
                  type="text"
                  className="form-input"
                  value={formData.yearStart}
                  onChange={(e) => updateFormData('yearStart', e.target.value)}
                  placeholder="2024"
                />
              </div>

              <div className="form-group">
                <label className="form-label">Fin</label>
                <input
                  type="text"
                  className="form-input"
                  value={formData.yearEnd}
                  onChange={(e) => updateFormData('yearEnd', e.target.value)}
                  placeholder="En cours"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  // Rendu de la liste
  const renderList = () => (
    <div className="projects-list-container">
      {/* Header */}
      <div className="list-header">
        <div className="list-header-left">
          <h1>Projets</h1>
          <span className="count-badge">{projects.length}</span>
        </div>

        <button className="btn btn-primary" onClick={handleNewProject}>
          <FiPlus />
          Nouveau projet
        </button>
      </div>

      {/* Filtres */}
      <div className="list-filters">
        <div className="search-box">
          <FiSearch />
          <input
            type="text"
            placeholder="Rechercher un projet..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          {searchTerm && (
            <button className="clear-search" onClick={() => setSearchTerm('')}>
              <FiX />
            </button>
          )}
        </div>

        <div className="filter-group">
          <select
            className="filter-select"
            value={filterCategory}
            onChange={(e) => setFilterCategory(e.target.value)}
          >
            <option value="all">Toutes catégories</option>
            {CATEGORIES.map(cat => (
              <option key={cat.value} value={cat.value}>{cat.label}</option>
            ))}
          </select>

          <select
            className="filter-select"
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
          >
            <option value="all">Tous statuts</option>
            {STATUS_OPTIONS.map(opt => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
        </div>

        {/* View toggle - caché sur mobile via CSS */}
        <div className="view-toggle">
          <button
            className={`view-btn ${view === 'list' ? 'active' : ''}`}
            onClick={() => setView('list')}
          >
            <FiList />
          </button>
          <button
            className={`view-btn ${view === 'grid' ? 'active' : ''}`}
            onClick={() => setView('grid')}
          >
            <FiGrid />
          </button>
        </div>
      </div>

      {/* Contenu */}
      {loading ? (
        <div className="loading-state">
          <FiLoader className="spinner" />
          <p>Chargement des projets...</p>
        </div>
      ) : filteredProjects.length === 0 ? (
        <div className="empty-state">
          <FiFolder />
          <h3>Aucun projet</h3>
          <p>
            {projects.length === 0 
              ? "Commencez par créer votre premier projet"
              : "Aucun projet ne correspond à vos critères"
            }
          </p>
          {projects.length === 0 && (
            <button className="btn btn-primary" onClick={handleNewProject}>
              <FiPlus />
              Créer un projet
            </button>
          )}
        </div>
      ) : (
        <>
          {/* Sur mobile : toujours afficher la grille */}
          {/* Sur desktop : afficher selon le mode sélectionné */}
          
          {/* Vue Grille - Affichée sur mobile OU quand view === 'grid' */}
          {(isMobile || view === 'grid') && (
            <div className="projects-grid">
              {filteredProjects.map(project => (
                <ProjectCard key={project.id} project={project} />
              ))}
            </div>
          )}

          {/* Vue Table - Affichée uniquement sur desktop quand view === 'list' */}
          {!isMobile && view === 'list' && (
            <table className="projects-table">
              <thead>
                <tr>
                  <th>Projet</th>
                  <th>Catégorie</th>
                  <th>Période</th>
                  <th>Statut</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredProjects.map(project => (
                  <tr key={project.id}>
                    <td>
                      <div className="project-cell">
                        <div className="project-thumb">
                          {project.mainImage ? (
                            <img src={project.mainImage} alt="" />
                          ) : (
                            <FiImage />
                          )}
                        </div>
                        <div className="project-info">
                          <strong>
                            {project.title}
                            {project.isFeatured && <FiStar className="featured-icon" />}
                          </strong>
                          <span>{project.shortDescription?.substring(0, 60)}...</span>
                        </div>
                      </div>
                    </td>
                    <td>
                      <span className="category-badge">
                        {CATEGORIES.find(c => c.value === project.category)?.label}
                      </span>
                    </td>
                    <td>
                      {project.yearStart}{project.yearEnd ? ` - ${project.yearEnd}` : ''}
                    </td>
                    <td>
                      <span className={`status-badge ${project.status}`}>
                        {STATUS_OPTIONS.find(s => s.value === project.status)?.label}
                      </span>
                    </td>
                    <td>
                      <div className="table-actions">
                        <button 
                          className="action-btn"
                          onClick={() => handleEditProject(project)}
                          title="Modifier"
                        >
                          <FiEdit2 />
                        </button>
                        <a 
                          href={`/projets/${project.slug}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="action-btn"
                          title="Voir"
                        >
                          <FiEye />
                        </a>
                        <button 
                          className="action-btn danger"
                          onClick={() => setShowDeleteConfirm(project.id)}
                          title="Supprimer"
                        >
                          <FiTrash2 />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </>
      )}

      {/* Modal de confirmation de suppression */}
      {showDeleteConfirm && (
        <div className="modal-overlay" onClick={() => setShowDeleteConfirm(null)}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <div className="modal-icon danger">
              <FiTrash2 />
            </div>
            <h3>Supprimer ce projet ?</h3>
            <p>Cette action est irréversible. Le projet sera définitivement supprimé.</p>
            <div className="modal-actions">
              <button 
                className="btn btn-ghost"
                onClick={() => setShowDeleteConfirm(null)}
              >
                Annuler
              </button>
              <button 
                className="btn btn-danger"
                onClick={() => handleDelete(showDeleteConfirm)}
              >
                <FiTrash2 />
                Supprimer
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );

  return (
    <AdminLayout>
      <div className="project-manager">
        {view === 'form' ? renderForm() : renderList()}
      </div>
    </AdminLayout>
  );
};

export default ProjectManager;
